/**
 * Akzentfarben für Projekte (MaterialTemplate.accentColor) — Karten-Rand +
 * getöntes Icon. Liegt in der ui-Lib, damit Projekte, Favoriten und Artikelsuche
 * sie ohne feature→feature-Import teilen können (vgl. Label-Farben in tag-colors).
 *
 * Hex-Werte 1:1 aus dem Claude-Design (`projekte.css`).
 */

import type { CSSProperties } from 'react';

export type AccentId = 'orange' | 'blue' | 'green' | 'violet' | 'amber';

export interface AccentOption {
  /** null = "Keine" (neutral) */
  id: AccentId | null;
  label: string;
  /** null bei "Keine" */
  hex: string | null;
}

/** Reihenfolge wie die Swatch-Reihe im Aktions-Sheet. */
export const ACCENT_OPTIONS: AccentOption[] = [
  { id: null, label: 'Keine', hex: null },
  { id: 'orange', label: 'Orange', hex: '#fa6223' },
  { id: 'blue', label: 'Blau', hex: '#226df8' },
  { id: 'green', label: 'Grün', hex: '#548f25' },
  { id: 'violet', label: 'Violett', hex: '#7b3fdb' },
  { id: 'amber', label: 'Bernstein', hex: '#d8920a' },
];

const ACCENT_HEX: Record<AccentId, string> = {
  orange: '#fa6223',
  blue: '#226df8',
  green: '#548f25',
  violet: '#7b3fdb',
  amber: '#d8920a',
};

/** Hex der Akzentfarbe oder null, wenn nicht/ungültig gesetzt. */
export function accentHex(accent: string | null | undefined): string | null {
  if (!accent) return null;
  return ACCENT_HEX[accent as AccentId] ?? null;
}

/**
 * Inline-Style für den linken Akzent-Rand einer Karte.
 * Ohne Akzent transparent — die Karte behält dann ihren neutralen Rahmen.
 */
export function accentBorderStyle(
  accent: string | null | undefined,
): CSSProperties {
  const hex = accentHex(accent);
  return {
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: hex ?? 'transparent',
  };
}

/**
 * Inline-Style für das getönte Karten-Icon: weicher Farb-Hintergrund (≈12%)
 * + volle Farbe als Vordergrund. Ohne Akzent neutrales Grau.
 */
export function accentIconStyle(
  accent: string | null | undefined,
): CSSProperties {
  const hex = accentHex(accent);
  if (!hex) {
    return { backgroundColor: '#f4f4f5', color: '#71717a' };
  }
  // `${hex}1f` ≈ 12% Deckkraft als getönter Hintergrund.
  return { backgroundColor: `${hex}1f`, color: hex };
}
